import { useMemo } from "react";
import {
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import CalendarCell from "src/components/TournamentScheduler/CalendarCell";

interface ScheduledGame {
  scheduled_event_id: number;
  club_id: number;
  tournament_group_id: number;
  tournament_id: number;
  home_team_id: number;
  away_team_id: number;
  game_date: string;
  game_time: string;
  duration_minutes: number;
  field_location: string;
  status: string;
  created_at: string;
}

interface ApprovedTeam {
  team_id: number;
  team_name: string;
  total_handicap: number;
  players: Array<{
    player_id: number;
    first_name: string;
    last_name: string;
    handicap: number;
    position: number;
  }>;
}

/* ------------------------------------------------------------------ */
/*  Overlap-aware layout computation                                    */
/* ------------------------------------------------------------------ */

export interface GameLayout {
  columnIndex: number;
  totalColumns: number;
}

/**
 * For every game, compute the column index and total number of columns
 * so that overlapping games are displayed side-by-side rather than
 * superimposed.
 */
function computeGameLayouts(games: ScheduledGame[]): Map<number, GameLayout> {
  const layoutMap = new Map<number, GameLayout>();

  // Group games by date
  const byDate: Record<string, ScheduledGame[]> = {};
  games.forEach((g) => {
    (byDate[g.game_date] ??= []).push(g);
  });

  for (const dateGames of Object.values(byDate)) {
    // Sort by start time, then by duration descending (longer games first)
    const sorted = [...dateGames].sort((a, b) => {
      const cmp = a.game_time.localeCompare(b.game_time);
      return cmp !== 0 ? cmp : b.duration_minutes - a.duration_minutes;
    });

    // Build overlap clusters: groups of games where every game overlaps
    // with at least one other game in the group.
    const clusters: ScheduledGame[][] = [];
    let currentCluster: ScheduledGame[] = [];
    let clusterEnd = dayjs("1970-01-01T00:00");

    for (const game of sorted) {
      const gStart = dayjs(`${game.game_date}T${game.game_time}`);
      const gEnd = gStart.add(game.duration_minutes, "minute");

      if (currentCluster.length === 0 || gStart.isBefore(clusterEnd)) {
        // Overlaps with the current cluster
        currentCluster.push(game);
        if (gEnd.isAfter(clusterEnd)) clusterEnd = gEnd;
      } else {
        // No overlap – start a new cluster
        clusters.push(currentCluster);
        currentCluster = [game];
        clusterEnd = gEnd;
      }
    }
    if (currentCluster.length > 0) clusters.push(currentCluster);

    // For each cluster assign column indices greedily
    for (const cluster of clusters) {
      // columns[i] = end time of the game currently occupying column i
      const columns: dayjs.Dayjs[] = [];
      const assignments: { game: ScheduledGame; col: number }[] = [];

      for (const game of cluster) {
        const gStart = dayjs(`${game.game_date}T${game.game_time}`);
        const gEnd = gStart.add(game.duration_minutes, "minute");

        // Find first column whose current occupant has ended
        let col = -1;
        for (let i = 0; i < columns.length; i++) {
          if (!gStart.isBefore(columns[i])) {
            col = i;
            break;
          }
        }
        if (col === -1) {
          col = columns.length;
          columns.push(gEnd);
        } else {
          columns[col] = gEnd;
        }

        assignments.push({ game, col });
      }

      const totalCols = columns.length;
      for (const { game, col } of assignments) {
        layoutMap.set(game.scheduled_event_id, { columnIndex: col, totalColumns: totalCols });
      }
    }
  }

  return layoutMap;
}

/* ------------------------------------------------------------------ */

interface CalendarTableProps {
  weekDates: string[];
  timeSlots: string[];
  allScheduledGames: ScheduledGame[];
  teamName: (id: number) => string;
  leagueNameMap: Record<number, { name: string; groupId: number }>;
  tournamentNameMap: Record<number, string>;
  teamsMap: Record<number, ApprovedTeam>;
  getPlayerIdsOnTeam: (id: number) => number[];
  openGameDetails: (game: ScheduledGame) => void;
}

function CalendarTable({
  weekDates,
  timeSlots,
  allScheduledGames,
  teamName,
  leagueNameMap,
  tournamentNameMap,
  teamsMap,
  getPlayerIdsOnTeam,
  openGameDetails,
}: CalendarTableProps) {
  // Pre-compute overlap-aware layout for all games
  const gameLayoutMap = useMemo(
    () => computeGameLayouts(allScheduledGames),
    [allScheduledGames]
  );
    return (
        <TableContainer component={Paper}>
            <Table stickyHeader size="small">
                <TableHead>
                    <TableRow>
                        <TableCell
                            sx={{
                                minWidth: 60,
                                width: 60,
                                backgroundColor: "background.paper",
                                zIndex: 3,
                            }}
                        >
                            <Typography variant="caption" fontWeight={600}>
                                Time
                            </Typography>
                        </TableCell>
                        {weekDates.map((date) => (
                            <TableCell
                                key={date}
                                align="center"
                                sx={{
                                    minWidth: 120,
                                    backgroundColor: "background.paper",
                                    zIndex: 2,
                                }}
                            >
                                <Stack spacing={0.5}>
                                    <Typography variant="caption" fontWeight={600}>
                                        {dayjs(date).format("ddd")}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {dayjs(date).format("MMM D")}
                                    </Typography>
                                </Stack>
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {timeSlots.map((slot) => {
                        // Check if this is a :00 or :30 slot for styling
                        const isHalfHour = slot.endsWith(":30");
                        // Extract hour to determine alternating background
                        const hour = parseInt(slot.split(":")[0]);
                        const isEvenHour = hour % 2 === 0; // 7am=odd (white), 8am=even (gray), 9am=odd (white)
                        const rowBg = isEvenHour ? "rgba(0, 0, 0, 0.02)" : "transparent";
                        const timeCellBg = isEvenHour ? "grey.200" : "grey.50";

                        return (
                            <TableRow
                                key={slot}
                                sx={{
                                    position: "relative",
                                    height: 28, // Half the height since we have 30-min slots
                                    backgroundColor: rowBg,
                                    borderBottom: isHalfHour
                                        ? "1px solid rgba(0, 0, 0, 0.06)"
                                        : "1px solid rgba(0, 0, 0, 0.12)",
                                }}
                            >
                                <TableCell
                                    sx={{
                                        backgroundColor: timeCellBg,
                                        fontWeight: isHalfHour ? 400 : 500,
                                        position: "sticky",
                                        left: 0,
                                        zIndex: 1,
                                        height: 28,
                                        borderBottom: isHalfHour
                                            ? "1px solid rgba(0, 0, 0, 0.06)"
                                            : "1px solid rgba(0, 0, 0, 0.12)",
                                    }}
                                >
                                    {!isHalfHour && (
                                        <Typography variant="caption">{slot}</Typography>
                                    )}
                                </TableCell>
                                {weekDates.map((date) => {
                                    // Find games that START at this time slot (not just overlap)
                                    const startingGames = allScheduledGames.filter(
                                        (g) => g.game_date === date && g.game_time === slot
                                    );
                                    return (
                                        <CalendarCell
                                            key={`${date}-${slot}`}
                                            date={date}
                                            time={slot}
                                            games={startingGames}
                                            teamName={teamName}
                                            leagueNameMap={leagueNameMap}
                                            tournamentNameMap={tournamentNameMap}
                                            allScheduledGames={allScheduledGames}
                                            teamsMap={teamsMap}
                                            getPlayerIdsOnTeam={getPlayerIdsOnTeam}
                                            hasSchedule={allScheduledGames.length > 0}
                                            rowBackground={rowBg}
                                            onGameClick={openGameDetails}
                                            gameLayoutMap={gameLayoutMap}
                                        />
                                    );
                                })}
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    );
}

export default CalendarTable;